import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Mic, Send, Sparkles } from "lucide-react";
import { sendChat } from "../api/client";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

export default function ChatPanel({ compact = false, autoGreet = true }) {
  const { lang, deviceId } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const reply = await sendChat(next, deviceId, lang);
      setMessages([...next, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: "\u2014" }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = new SR();
    rec.lang = lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US";
    rec.interimResults = false;
    rec.onresult = (e) => setInput(e.results[0][0].transcript);
    rec.onend = () => setListening(false);
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  const suggestions = t(lang, "suggestions");

  return (
    <div className={`glass-strong rounded-3xl overflow-hidden flex flex-col ${compact ? "" : "h-full"}`}>
      {/* Messages */}
      <div ref={scrollRef} className={`px-5 md:px-6 py-5 overflow-y-auto flex flex-col gap-4 ${compact ? "max-h-[42vh] min-h-[220px]" : "flex-1"}`}>
        {autoGreet && (
          <div className="self-start max-w-[92%] rounded-2xl rounded-tl-sm px-4 py-3 fade-in" style={{ background: "rgba(255,246,222,0.9)", border: "1px solid rgba(201,149,33,0.3)" }}>
            <p className="font-scripture text-[1.02rem] leading-relaxed text-[var(--ink-soft)]">{t(lang, "greeting")}</p>
          </div>
        )}
        {messages.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="self-end max-w-[85%] rounded-2xl rounded-tr-sm px-4 py-2.5 fade-up btn-gold">
              <p className="text-sm font-medium">{m.content}</p>
            </div>
          ) : (
            <div key={i} className="self-start max-w-[95%] rounded-2xl rounded-tl-sm px-4 py-3 fade-up" style={{ background: "rgba(255,255,255,0.92)", border: "1px solid rgba(150,190,225,0.5)" }}>
              <div className="md text-[0.98rem]">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
              </div>
            </div>
          )
        )}
        {loading && (
          <div className="self-start flex items-center gap-2 text-[var(--gold-deep)] px-2 fade-in">
            <Sparkles size={16} className="animate-pulse" />
            <span className="text-sm italic font-scripture">{t(lang, "thinking")}</span>
          </div>
        )}
      </div>

      {/* Suggestions */}
      {messages.length === 0 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2 justify-center">
          {suggestions.map((s) => (
            <button key={s} onClick={() => send(s)} className="btn-ghost-gold text-xs px-3 py-1.5 rounded-full">
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-3 md:p-4 border-t border-[rgba(210,170,80,0.25)] flex items-center gap-2">
        <button
          onClick={toggleMic}
          className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all ${listening ? "btn-gold animate-pulse" : "btn-ghost-gold"}`}
          aria-label="Voice input"
        >
          <Mic size={18} />
        </button>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={t(lang, "placeholder")}
          className="flex-1 bg-white/85 border border-[rgba(150,190,225,0.6)] rounded-full px-5 py-3 text-[var(--ink)] placeholder:text-[var(--ink-faint)] outline-none focus:border-[var(--gold)] focus:ring-2 focus:ring-[rgba(255,214,120,0.4)] transition"
        />
        <button onClick={() => send()} disabled={loading} className="btn-gold shrink-0 rounded-full px-5 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
          <Send size={16} />
          <span className="hidden sm:inline">{t(lang, "send")}</span>
        </button>
      </div>
    </div>
  );
}
